import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { GoogleService } from 'src/app/services/google.service';
import {
  HubspotNewCompany,
  HubspotNewCompanyFailure,
  HubspotNewCompanySuccess,
  HubspotNewContact,
  HubspotNewContactFailure,
  HubspotNewContactSuccess,
  getHubspotCompanies,
  getHubspotCompaniesFailure,
  getHubspotCompaniesSuccess,
  getHubspotCompany,
  getHubspotCompanyFailure,
  getHubspotCompanyIndustries,
  getHubspotCompanyIndustriesFailure,
  getHubspotCompanyIndustriesSuccess,
  getHubspotCompanyLifeCycleStages,
  getHubspotCompanyLifeCycleStagesFailure,
  getHubspotCompanyLifeCycleStagesSuccess,
  getHubspotCompanySuccess,
  getHubspotContact,
  getHubspotContactFailure,
  getHubspotContactSuccess,
  getHubspotDealOwners,
  getHubspotDealOwnersFailure,
  getHubspotDealOwnersSuccess,
  getHubspotDealPipelines,
  getHubspotDealPipelinesFailure,
  getHubspotDealPipelinesSuccess,
  getHubspotDealStages,
  getHubspotDealStagesFailure,
  getHubspotDealStagesSuccess,
  getHubspotDeals,
  getHubspotDealsFailure,
  getHubspotDealsSuccess,
  hubspotNewDeal,
  hubspotNewDealFailure,
  hubspotNewDealSuccess,
  showMoreHubspotDeals,
  showMoreHubspotDealsFailure,
  showMoreHubspotDealsSuccess,
} from './hubspot.actions';
import { HubspotService } from 'src/app/services/hubspot.service';

@Injectable()
export class HubspotEffects {
  hubspotGetContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotContact), // Listen for the hubspot Callback action
      switchMap(({ search_id }) =>
        this.hubspotService.showContact(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No contact found');
            }
            return getHubspotContactSuccess({ hubspot_contact: response.data });
          }),
          catchError((error) => {
            console.log('Hubspot Get Contact failed', error);
            return of(getHubspotContactFailure({ error: error }));
          })
        )
      )
    )
  );

  hubspotNewContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HubspotNewContact), // Listen for the hubspot Callback action

      switchMap(({ name, mail, company, phone, user_id }) =>
        this.hubspotService
          .newContact(user_id, mail, company, phone, name)
          .pipe(
            map((response) => {
              if (!response || !response.data) {
                throw new Error('Unexpected Structure');
              }
              return HubspotNewContactSuccess({
                hubspot_new_contact: response.data,
              });
            }),
            catchError((error) => {
              console.log('Hubspot New Contact creation failed', error);
              return of(HubspotNewContactFailure({ error }));
            })
          )
      )
    )
  );

  hubspotNewCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HubspotNewCompany), // Listen for the hubspot Callback action

      switchMap(
        ({
          user_id,
          company_name,
          company_domain,
          company_industry,
          company_city,
          company_phone,
          company_state,
          company_life_cycle_stage,
          contact_id,
        }) =>
          this.hubspotService
            .newCompany(
              user_id,
              company_name,
              company_domain,
              company_industry,
              company_phone,
              company_city,
              company_state,
              company_life_cycle_stage,
              contact_id
            )
            .pipe(
              map((response) => {
                if (!response || !response.data) {
                  throw new Error('Unexpected Structure');
                }
                return HubspotNewCompanySuccess({
                  hubspot_new_company: response.data,
                });
              }),
              catchError((error) => {
                console.log('Hubspot New Company creation failed', error);
                return of(HubspotNewCompanyFailure({ error }));
              })
            )
      )
    )
  );

  hubspotGetCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotCompany), // Listen for the hubspot Callback action

      switchMap(({ contact_id, search_id }) =>
        this.hubspotService.showCompany(contact_id, search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No company found');
            }
            return getHubspotCompanySuccess({ hubspot_company: response.data });
          }),
          catchError((error) => {
            console.log('Hubspot Get Company failed', error);
            return of(getHubspotCompanyFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotCompanies), // Listen for the hubspot Callback action

      switchMap(({ search_id }) =>
        this.hubspotService.getHubspotCompanies(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No companies found');
            }
            return getHubspotCompaniesSuccess({
              hubspot_companies: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get Company failed', error);
            return of(getHubspotCompaniesFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetCompanyLifeCycleStages = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotCompanyLifeCycleStages), // Listen for the hubspot Callback action

      switchMap(({ search_id }) =>
        this.hubspotService.getHubspotCompanyLifeCycleStages(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No company life cycle stages  found');
            }
            return getHubspotCompanyLifeCycleStagesSuccess({
              hubspot_company_life_cycle_stages: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get company life cycle stages failed', error);
            return of(getHubspotCompanyLifeCycleStagesFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetCompanyIndustries = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotCompanyIndustries), // Listen for the hubspot Callback action

      switchMap(({ search_id }) =>
        this.hubspotService.getHubspotCompanyIndustries(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No company industries  found');
            }
            return getHubspotCompanyIndustriesSuccess({
              hubspot_company_industries: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get company Industries failed', error);
            return of(getHubspotCompanyIndustriesFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetDealOwners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotDealOwners), // Listen for the hubspot Callback action

      switchMap(({ search_id }) =>
        this.hubspotService.getHubspotDealOwners(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No deal Owners found');
            }
            return getHubspotDealOwnersSuccess({
              hubspot_deal_owners: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get Deal Pipelines failed', error);
            return of(getHubspotDealOwnersFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetDealPipelines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotDealPipelines), // Listen for the hubspot Callback action

      switchMap(({ search_id }) =>
        this.hubspotService.getHubspotDealPipelines(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No deal pipelines  found');
            }
            return getHubspotDealPipelinesSuccess({
              hubspot_deal_pipelines: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get Deal Pipelines failed', error);
            return of(getHubspotDealPipelinesFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetDealStages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotDealStages), // Listen for the hubspot Callback action

      switchMap(({ search_id, pipeline }) =>
        this.hubspotService.getHubspotDealStages(search_id, pipeline).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('No deal stages  found');
            }
            return getHubspotDealStagesSuccess({
              hubspot_deal_stages: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Get Deal Stages failed', error);
            return of(getHubspotDealStagesFailure({ error }));
          })
        )
      )
    )
  );

  hubspotGetDeals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getHubspotDeals), // Listen for the hubspot Callback action

      switchMap(({ contact_id, search_id }) =>
        this.hubspotService.showDeals(contact_id, search_id).pipe(
          map((response) => {
            // A contact with no associated deals is a normal empty state, not an
            // error — the API returns [] (or 0) in that case. Normalize it to an
            // empty list so the UI shows "no deals" instead of logging a failure.
            const data = response?.data;
            const deals = data && data.deals ? data : { deals: [], total: 0 };
            return getHubspotDealsSuccess({ hubspot_deals: deals });
          }),
          catchError((error) => {
            console.error('Hubspot Get Deals failed', error);
            return of(getHubspotDealsFailure({ error }));
          })
        )
      )
    )
  );

  hubspotShowMoreDeals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showMoreHubspotDeals), // Listen for the hubspot Callback action

      switchMap(({ contact_id, search_id, page }) =>
        this.hubspotService.showMoreDeals(contact_id, search_id, page).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('Invalid Hubspot response');
            }
            return showMoreHubspotDealsSuccess({
              hubspot_deals: response.data,
            });
          }),
          catchError((error) => {
            console.log('Hubspot Show More Deals failed', error);
            return of(showMoreHubspotDealsFailure({ error }));
          })
        )
      )
    )
  );

  hubspotNewDeal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hubspotNewDeal), // Listen for the hubspot Callback action

      switchMap(({ user_id, properties, contact_id }) =>
        this.hubspotService.newDeal(user_id, properties, contact_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error

              console.log('Invalid hubspot response: ', response);
              throw new Error('Invalid Hubspot response');
            }
            return hubspotNewDealSuccess({ hs_object: response.data });
          }),
          catchError((error) => {
            console.log('Hubspot New Deal failed', error);
            return of(hubspotNewDealFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private hubspotService: HubspotService
  ) {}
}
